import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskedLine, ImageReveal, Chapter, ParallaxBand } from "../components/Reveal";
import { IMG } from "../data/images";

const UNDERSTAND = [
  "Product", "Commercial requirements", "Supplier relationships", "Costing",
  "Quality", "Timelines", "Production realities",
];

export default function About() {
  return (
    <>
      <section data-testid="about-hero" className="bg-ivory pt-36 md:pt-48">
        <div className="mx-auto max-w-7xl px-6 pb-20 md:px-12 md:pb-28">
          <Reveal>
            <p className="font-label text-[11px] font-semibold uppercase tracking-[0.3em] text-stone-500">About Origin Bureau</p>
          </Reveal>
          <h1 className="mt-8 max-w-5xl font-serif text-5xl leading-[1.08] tracking-tight text-ink md:text-7xl">
            <MaskedLine delay={0.2}>Experienced enough to know</MaskedLine>
            <MaskedLine delay={0.32}>what can go wrong.</MaskedLine>
            <MaskedLine delay={0.44}>
              <span className="italic text-terra">Small enough to stay involved.</span>
            </MaskedLine>
          </h1>
        </div>
      </section>

      <section data-testid="about-story" className="bg-ivory pb-24 md:pb-36">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 md:grid-cols-12 md:px-12">
          <div className="md:col-span-7">
            <Chapter number="01" label="Why We Exist" />
            <Reveal delay={0.1}>
              <p className="mt-10 text-base leading-relaxed text-stone-600 md:text-lg">
                Origin Bureau exists to give growing brands access to the sourcing, merchandising, product
                development and production expertise they would otherwise need to build internally — at a
                fraction of the cost of a local office.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-base leading-relaxed text-stone-600 md:text-lg">
                We are not a faceless agency routing emails between continents. We are the person on the
                ground who understands both sides of the table: what your brand needs the product to be,
                and what the factory needs in order to make it well.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-6 text-base leading-relaxed text-stone-600 md:text-lg">
                Many sourcing agencies chase volume. We built Origin Bureau for the brands they overlook —
                brands launching first collections, testing new products and scaling from small quantities.
                Small MOQ. No small ambitions.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <ImageReveal src={IMG.leather} alt="Hands working leather with warm natural light" className="aspect-[4/5]" />
            <Reveal delay={0.2}>
              <p className="mt-4 font-label text-[10px] uppercase tracking-[0.26em] text-stone-500">
                Hands-on, by definition
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <ParallaxBand
        src={IMG.jewelryDetail}
        alt="Close-up of fine jewelry pieces, metal and stone details"
        caption="Details decide whether a product feels made — or merely manufactured"
      />
      <section data-testid="about-understand" className="border-y border-stone-200 bg-stone-100 py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Chapter number="02" label="What We Understand" />
          <div className="mt-14 grid gap-14 md:grid-cols-12">
            <div className="md:col-span-5">
              <Reveal>
                <h2 className="font-serif text-4xl leading-[1.1] tracking-tight text-ink md:text-5xl">
                  The bridge between <span className="italic text-terra">brand and factory.</span>
                </h2>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-8 max-w-md text-base leading-relaxed text-stone-600">
                  Working with us means working with people who have sat on both sides of the table — and
                  know how products actually get made, priced, delayed, fixed and shipped.
                </p>
              </Reveal>
            </div>
            <div className="md:col-span-7">
              <ul className="divide-y divide-stone-300 border-y border-stone-300">
                {UNDERSTAND.map((u, i) => (
                  <Reveal key={u} delay={0.05 * i} y={16}>
                    <li className="flex items-center justify-between py-5">
                      <span className="font-serif text-2xl text-stone-800 md:text-3xl">{u}</span>
                      <span className="font-serif text-sm italic text-terra">{String(i + 1).padStart(2, "0")}</span>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="about-india" className="noise-overlay bg-ink py-24 text-ivory md:py-36">
        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-16 px-6 md:grid-cols-12 md:px-12">
          <div className="md:col-span-6">
            <ImageReveal src={IMG.jewelry} alt="Jewelry making in progress, hands at work" className="aspect-[4/3]" />
          </div>
          <div className="md:col-span-6">
            <Reveal>
              <h2 className="font-serif text-4xl leading-[1.1] tracking-tight md:text-5xl">
                India is our network. <span className="italic text-terra-light">Your product is our focus.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-lg text-base leading-relaxed text-stone-400 md:text-lg">
                Factories, workshops, material suppliers and makers — relationships built over years, not
                directories scraped overnight. When you work with Origin Bureau, that network works for you.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <Link
                to="/contact"
                data-testid="about-cta-button"
                className="group mt-10 inline-flex items-center gap-3 bg-terra px-8 py-4 font-label text-xs font-semibold uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:bg-terra-dark"
              >
                Work With Us
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
